/**
 * Suppresses false-positive connectivity/auth snackbars during SPA bootstrap and
 * dedupes repeated global error snackbars (868kwqh8w).
 */

const DEFAULT_MAX_BOOTSTRAP_MS = 5000;
const DEFAULT_DEDUPE_WINDOW_MS = 10000;

class InitialLoadErrorGate {
    constructor() {
        this._ready = false;
        this._startedAt = 0;
        this._maxBootstrapMs = DEFAULT_MAX_BOOTSTRAP_MS;
        this._dedupeWindowMs = DEFAULT_DEDUPE_WINDOW_MS;
        this._recentSnackbarKeys = new Map();
        this._fallbackTimer = null;
    }

    start(maxBootstrapMs = DEFAULT_MAX_BOOTSTRAP_MS) {
        this._ready = false;
        this._startedAt = Date.now();
        this._maxBootstrapMs = maxBootstrapMs;

        if (this._fallbackTimer) {
            clearTimeout(this._fallbackTimer);
        }

        this._fallbackTimer = setTimeout(() => {
            this.markReady();
        }, this._maxBootstrapMs);
    }

    markReady() {
        this._ready = true;

        if (this._fallbackTimer) {
            clearTimeout(this._fallbackTimer);
            this._fallbackTimer = null;
        }
    }

    /** @param {{ forTests?: boolean }} [options] */
    reset(options = {}) {
        if (this._fallbackTimer) {
            clearTimeout(this._fallbackTimer);
            this._fallbackTimer = null;
        }

        this._ready = !options.forTests;
        this._startedAt = 0;
        this._recentSnackbarKeys.clear();
    }

    isBootstrapping() {
        if (this._ready || this._startedAt === 0) {
            return false;
        }

        if (Date.now() - this._startedAt > this._maxBootstrapMs) {
            return false;
        }

        return true;
    }

    /**
     * SessionMonitor / UnauthenticatedInterceptor own 401 UX — never duplicate unauthorized snackbars.
     */
    shouldSuppressUnauthorizedUserError(error) {
        return error?.response?.status === 401;
    }

    /**
     * Defer connectivity banners until bootstrap completes (failed /api/online, Network Error, etc.).
     */
    shouldSuppressConnectivityUserError(error) {
        if (!this.isBootstrapping()) {
            return false;
        }

        return this._isConnectivityFailure(error);
    }

    shouldSuppressOfflineSnackbar() {
        return this.isBootstrapping();
    }

    _isConnectivityFailure(error) {
        if (error?.response) {
            return false;
        }

        const url = error?.config?.url;

        if (typeof url === 'string' && url.includes('/api/online')) {
            return true;
        }

        const message = error?.message || '';

        if (message.includes('Network Error') || message.toLowerCase().includes('network error')) {
            return true;
        }

        return false;
    }

    /**
     * @param {import('axios').AxiosError|Error|null|undefined} error
     * @param {string|null|undefined} userMessage
     */
    getSnackbarDedupeKey(error, userMessage) {
        const status = error?.response?.status;

        if (status === 401) {
            return 'auth-unauthorized';
        }

        if (status === 403) {
            return 'auth-forbidden';
        }

        if (!error?.response && error?.message?.includes('Network Error')) {
            return 'network-error';
        }

        if (typeof userMessage === 'string' && userMessage.toLowerCase().includes('network is offline')) {
            return 'network-offline';
        }

        const messagePrefix = (userMessage || '').slice(0, 48);

        return `http-${status ?? 'none'}-${messagePrefix}`;
    }

    shouldDedupeSnackbar(dedupeKey) {
        const now = Date.now();
        this._pruneDedupeKeys(now);

        if (this._recentSnackbarKeys.has(dedupeKey)) {
            return true;
        }

        this._recentSnackbarKeys.set(dedupeKey, now);

        return false;
    }

    _pruneDedupeKeys(now) {
        for (const [key, timestamp] of this._recentSnackbarKeys.entries()) {
            if (now - timestamp > this._dedupeWindowMs) {
                this._recentSnackbarKeys.delete(key);
            }
        }
    }
}

export default new InitialLoadErrorGate();
